import FeatherIcon from "feather-icons-react";
import React, { useEffect, useRef, useState, forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styled, { css } from "styled-components";
import { colors } from "./colors";
import { Body1 } from "./Text";
import { enUS } from "date-fns/locale";
import { format, isToday, isYesterday, isTomorrow } from "date-fns";
import { toAbsoluteUrl } from "_metronic/_helpers";
import SVG from "react-inlinesvg";

const variantStyles = {
    normal: css`
        border: 1px solid ${colors.passive[300]};
        color: ${colors.passive[500]};
        padding: 10px 15px 10px 15px;
    `,
    focus: css`
        color: ${(props) => colors[props.componentVariant][900]};
        border: 2px solid ${(props) => colors[props.componentVariant][900]};
        padding: 10px 14px;
    `,
    filled: css`
        color: ${(props) => colors[props.componentVariant][900]};
        border: 1px solid ${colors.passive[300]};
        padding: 10px 15px 10px 15px;
    `,
    disabled: css`
        border-color: ${colors.neutral[300]};
        color: ${colors.neutral[300]};
        background-color: #f6f8f9;
    `,
    error: css`
        border-color: #e74c3c;
        color: #e74c3c;
    `,
};

const DatePickerWrapper = styled.div`
    background-color: #fff;
    display: flex;
    align-items: center;
    border: 1px solid ${colors.neutral[500]};
    border-radius: 8px;
    padding: 10px 5px 10px 0px;
    height: 41px;
    font-size: 16px;
    input {
        &::placeholder {
            color: ${colors.neutral[500]};
        }
        color: ${colors.neutral[800]};
    }
    .react-datepicker {
        font-weight: 500;
        border-radius: 8px;
        color: ${colors.neutral[900]} !important;
        border: 1px solid ${colors.white} !important;
        box-shadow: 0px 2px 8px rgba(73, 105, 140, 0.1);
        right: 20px;
        font-family: "Poppins_PL";
    }
    .react-datepicker__month-container {
        padding: 10px;
    }
    .react-datepicker-popper {
        margin-top: 5px;
    }
    .react-datepicker__triangle {
        display: none;
    }
    .react-datepicker__header {
        border-bottom: none;
        background-color: ${colors.white};
        .react-datepicker__navigation-icon::before {
            color: ${colors.neutral[900]};
        }
        .react-datepicker__current-month {
            color: ${colors.neutral[900]};
            font-size: 14px;
            font-weight: normal;
            margin-top: 10px;
            margin-bottom: 18px;
        }
        .react-datepicker__day-name {
            color: ${colors.neutral[700]};
            font-size: 12px;
            width: 32px;
            margin: 0;
            height: 32px;
        }
    }
    .react-datepicker__time-container {
        width: 93px;
        border-radius: 4px;
        .react-datepicker__time-list {
            margin-top: 10px !important;
        }
        .react-datepicker-time__header {
            display: none;
        }
        .react-datepicker__time-list-item {
            margin-right: 3px;
            padding: 0 !important;
            display: flex;
            font-size: 12px !important;
            height: 19px !important;
            padding-top: 15px !important;
            padding-bottom: 15px !important;
            justify-content: center;
            align-items: center;
            font-weight: 400;
            &:hover {
                color: ${colors.neutral[900]} !important;
                background-color: #f6f8f9 !important;
                border-radius: 4px;
                color: ${colors.neutral[900]};
                font-size: 12px;
                font-weight: 500;
            }
        }
        .react-datepicker__time-list-item--selected {
            color: ${(props) => colors[props.componentVariant][50]} !important;
            background-color: ${(props) => colors[props.componentVariant][900]} !important;
            border-radius: 4px !important;
            font-weight: normal !important;
        }
        .react-datepicker__time-list::-webkit-scrollbar {
            width: 8px;
        }
        .react-datepicker__time-list::-webkit-scrollbar-track {
            border-radius: 10px;
        }
        .react-datepicker__time-list::-webkit-scrollbar-thumb {
            background: ${colors.passive[500]};
            border-radius: 10px;
        }
    }
    .react-datepicker__day--today {
        background: none;
        color: ${colors.neutral[900]} !important;
        font-size: 12px;
    }
    .react-datepicker__day--keyboard-selected {
        color: ${(props) => colors[props.componentVariant][50]} !important;
        background-color: ${(props) => colors[props.componentVariant][200]};
        border-radius: 50%;
    }
    .react-datepicker__day--selected {
        color: ${(props) => colors[props.componentVariant][50]} !important;
        background-color: ${(props) => colors[props.componentVariant][900]};
        border-radius: 50%;
    }

    .react-datepicker__day:hover {
        color: ${colors.neutral[900]} !important;
        background-color: #f6f8f9 !important;
        border-radius: 50%;
    }

    .react-datepicker__day {
        color: ${colors.neutral[900]};
        font-size: 12px;
        width: 26px;
        height: 26px;
        margin: 4px;
    }
    .react-datepicker__day--outside-month {
        color: ${colors.neutral[500]};
        font-size: 12px;
    }
    .react-datepicker-wrapper {
        width: 100%;
    }

    .react-datepicker__input-container {
        width: 100%;

        input {
            width: 100%;
        }
    }
    .react-datepicker__header,
    .react-datepicker__header--custom {
        padding: 0;
    }
    ${({ variant }) => variantStyles[variant]}
    .react-datepicker__day--highlighted {
        color: ${colors.warning[900]};
        background-color: transparent !important;
        border-bottom: 1px solid ${colors.warning[900]} !important;
        border-radius: 0 !important;
        font-weight: 500;

        &:hover {
            border-radius: 0 !important;
            background-color: transparent !important;
            color: ${colors.warning[900]};
        }
    }
`;

const UnStyledDatePickerWrapper = styled.div`
    display: flex;
    align-items: center;
    .react-datepicker {
        font-weight: 500;
        border-radius: 8px;
        color: ${colors.neutral[900]} !important;
        border: 1px solid ${colors.white} !important;
        box-shadow: 0px 2px 8px rgba(73, 105, 140, 0.1);
        font-family: "Poppins_PL";
        z-index: 999999 !important;
    }
    .react-datepicker__month-container {
        padding: 10px;
    }
    .react-datepicker-popper {
        margin-top: 5px;
        z-index: 999999 !important;
        transform: translate(14px, 30px) !important;
    }
    .react-datepicker__triangle {
        display: none;
    }
    .react-datepicker__header {
        border-bottom: none;
        background-color: ${colors.white};
        .react-datepicker__navigation-icon::before {
            color: ${colors.neutral[900]};
        }
        .react-datepicker__current-month {
            color: ${colors.neutral[900]};
            font-size: 14px;
            font-weight: normal;
            margin-top: 10px;
            margin-bottom: 18px;
        }
        .react-datepicker__day-name {
            color: ${colors.neutral[700]};
            font-size: 12px;
            width: 32px;
            margin: 0;
            height: 32px;
        }
    }
    .react-datepicker__time-container {
        width: 93px;
        border-radius: 4px;
        .react-datepicker__time-list {
            margin-top: 10px !important;
        }
        .react-datepicker-time__header {
            display: none;
        }
        .react-datepicker__time-list-item {
            padding: 0 !important;
            display: flex;
            font-size: 12px !important;
            height: 19px !important;
            padding-top: 12px !important;
            padding-bottom: 12px !important;
            justify-content: center;
            align-items: center;
            font-weight: 400;
            &:hover {
                color: ${colors.neutral[900]} !important;
                background-color: #f6f8f9 !important;
                border-radius: 4px;
                color: ${colors.neutral[900]};
                font-size: 12px;
                font-weight: 500;
            }
        }
        .react-datepicker__time-list-item--selected {
            color: ${(props) => colors[props.componentVariant][50]} !important;
            background-color: ${(props) => colors[props.componentVariant][900]} !important;
            border-radius: 4px !important;
            font-weight: normal !important;
        }
        .react-datepicker__time-list::-webkit-scrollbar {
            width: 8px;
        }
        .react-datepicker__time-list::-webkit-scrollbar-track {
            border-radius: 10px;
        }
        .react-datepicker__time-list::-webkit-scrollbar-thumb {
            background: ${colors.passive[500]};
            border-radius: 10px;
        }
    }
    .react-datepicker__day--today {
        background: none;
        color: ${colors.neutral[900]} !important;
        font-size: 12px;
    }
    .react-datepicker__day--keyboard-selected {
        color: ${(props) => colors[props.componentVariant][50]} !important;
        background-color: ${(props) => colors[props.componentVariant][200]};
        border-radius: 50%;
    }
    .react-datepicker__day--selected {
        color: ${(props) => colors[props.componentVariant][50]} !important;
        background-color: ${(props) => colors[props.componentVariant][900]};
        border-radius: 50%;
    }

    .react-datepicker__day:hover {
        color: ${colors.neutral[900]} !important;
        background-color: #f6f8f9 !important;
        border-radius: 50%;
    }

    .react-datepicker__day {
        color: ${colors.neutral[900]};
        font-size: 12px;
        width: 26px;
        height: 26px;
        margin: 4px;
    }
    .react-datepicker__day--outside-month {
        color: ${colors.neutral[500]};
        font-size: 12px;
    }
    .react-datepicker-wrapper {
        flex: 1;
        min-width: 0;
        line-height: 1;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .special-day {
        background-color: black;
    }
    .react-datepicker__input-container {
        flex: 1;
        min-width: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        line-height: inherit;
        input {
            width: 100%;
        }
    }
    .react-datepicker__header,
    .react-datepicker__header--custom {
        padding: 0;
    }
    ${({ variant }) => variantStyles[variant]}
    .react-datepicker__day--highlighted {
        color: ${colors.warning[900]} !important;
        background-color: transparent !important;
        border-bottom: 1px solid ${colors.warning[900]} !important;
        border-radius: 0 !important;
        font-weight: 500;

        &:hover {
            border-radius: 0 !important;
            background-color: transparent !important;
            color: ${colors.warning[900]};
        }
    }
`;

const StyledDatePicker = styled(DatePicker)`
    width: 100%;
    background-color: #fff;
    display: flex;
    align-items: center;
    border: none;
    outline: none;
    color: ${colors.neutral[500]};
    text-color: ${colors.neutral[500]};
`;

const UnStyledDatePicker = styled(DatePicker)`
    //width: 100%;
    background-color: inherit;
    display: flex;
    align-items: center;
    border: none;
    outline: none;
    color: ${colors.neutral[900]};
    font-size: 16px;
    font-weight: 500;
    line-height: inherit;
    padding: 0;
`;

const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    width: 20px;
    height: 20px;
    cursor: pointer;

    svg {
        color: ${({ filled, focus, props }) =>
            filled
                ? `${(props) => colors[props.componentVariant][900]}`
                : focus
                ? `${(props) => colors[props.componentVariant][900]}`
                : `${colors.neutral[500]}`};

        &:hover {
            color: ${(props) => colors[props.componentVariant][900]};
        }
    }
`;

const StyledDatePickerHeaderButton = styled("div")`
    background: "none";
    border: "none";
    cursor: "pointer";
    font-size: "18px";
    color: ${colors.neutral[900]};
`;
const StyledDatePickerHeader = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 8px;
    margin-right: 8px;
    margin-top: 6px;
    margin-bottom: 16px;
`;
const StyledMonthName = styled(Body1)`
    font-family: "Poppins_PL";
    font-weight: normal;
`;

const StyledHeaderDropdownContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const StyledHeaderSelect = styled.select`
    font-family: "Poppins_PL";
    font-size: 14px;
    font-weight: 500;
    color: ${colors.neutral[900]};
    background-color: transparent;
    border: none;
    cursor: pointer;
    outline: none;
    padding: 4px 8px;
    border-radius: 4px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 4px center;
    background-size: 12px;
    padding-right: 20px;

    &:hover {
        background-color: ${colors.neutral[100]};
    }

    &:focus {
        background-color: ${colors.neutral[100]};
    }

    option {
        font-family: "Poppins_PL";
        font-size: 14px;
        color: ${colors.neutral[900]};
        background-color: white;
        padding: 8px;
    }
`;
export const Seperator = styled.div`
    width: 1px;
    height: 100%;
    margin-left: 10px;
    margin-right: 10px;
    background-color: ${({ isDisabled }) => (isDisabled ? `${colors.neutral[500]}` : `${colors.neutral[300]}`)};
`;

const DayNavBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background-color: transparent;
    border: none;
    border-radius: 8px;
    padding: 10px 0px 10px 5px;
    height: 41px;
    font-size: 16px;
    box-sizing: border-box;
`;

const DayNavArrowButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: ${colors.neutral[900]};
    flex-shrink: 0;
    width: 32px;
    min-width: 32px;

    &:hover {
        opacity: 0.7;
    }
    &:focus {
        outline: none;
    }
`;

const DayNavDateText = styled.span`
    font-size: 14px;
    white-space: nowrap;
    text-align: center;
    color: ${colors.neutral[800]};
    font-weight: 500;
    font-family: "Poppins_PL", sans-serif;
    ${({ $clickable }) => $clickable && "cursor: pointer;"}
`;

const MoveDropdownDatePickerWrapper = styled.div`
    .react-datepicker {
        font-weight: 500;
        border-radius: 8px;
        color: ${colors.neutral[900]} !important;
        border: 1px solid ${colors.white} !important;
        box-shadow: 0px 2px 8px rgba(73, 105, 140, 0.1);
        font-family: "Poppins_PL";
    }
    .react-datepicker__month-container {
        padding: 10px;
    }
    .react-datepicker-popper {
        position: relative !important;
        transform: none !important;
        margin-top: 5px;
    }
    .react-datepicker__triangle {
        display: none;
    }
    .react-datepicker__header {
        border-bottom: none;
        background-color: ${colors.white};
        .react-datepicker__navigation-icon::before {
            color: ${colors.neutral[900]};
        }
        .react-datepicker__current-month {
            color: ${colors.neutral[900]};
            font-size: 14px;
            font-weight: normal;
            margin-top: 10px;
            margin-bottom: 18px;
        }
        .react-datepicker__day-name {
            color: ${colors.neutral[700]};
            font-size: 12px;
            width: 32px;
            margin: 0;
            height: 32px;
        }
    }
    .react-datepicker__day--keyboard-selected {
        color: ${(props) => colors[props.componentVariant][50]} !important;
        background-color: ${(props) => colors[props.componentVariant][200]};
        border-radius: 50%;
    }
    .react-datepicker__day--today {
        background: none;
        color: ${colors.neutral[900]} !important;
        font-size: 12px;
    }
    .react-datepicker__day--selected {
        color: ${(props) => colors[props.componentVariant][50]} !important;
        background-color: ${(props) => colors[props.componentVariant][900]};
        border-radius: 50%;
    }
    .react-datepicker__day:hover {
        color: ${colors.neutral[900]} !important;
        background-color: #f6f8f9 !important;
        border-radius: 50%;
    }
    .react-datepicker__day {
        color: ${colors.neutral[900]};
        font-size: 12px;
        width: 26px;
        height: 26px;
        margin: 4px;
    }
    .react-datepicker__day--outside-month {
        color: ${colors.neutral[500]};
        font-size: 12px;
    }
    .react-datepicker__input-container {
        display: none;
    }
`;

const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const getYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 10; i <= currentYear + 10; i++) {
        years.push(i);
    }
    return years;
};

const getDayNavigationLabel = (date) => {
    if (isToday(date)) return "Today";
    if (isYesterday(date)) return "Yesterday";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "EEE", { locale: enUS });
};

const formatDateForHeader = (date) => {
    if (!date) return "";
    const dayLabel = getDayNavigationLabel(date);
    const datePart = format(date, "dd MMM yyyy", { locale: enUS });
    return `${dayLabel}, ${datePart}`;
};

const CustomDatePicker = forwardRef(
    (
        {
            timeStamp,
            leftIcon,
            rightIcon,
            disabled,
            value,
            onChange,
            variant,
            highlightedDates,
            leftIconClick,
            dateFormat,
            noTimeSelection,
            componentVariant = "primary",
            showMonthYearDropdown = false,
            ...props
        },
        ref
    ) => {
        const [isFocused, setIsFocused] = useState(false);
        const datePickerRef = useRef();
        const pickerRef = useRef(null);
        const [date, setDate] = useState(value ? new Date(value) : null);
        const decreaseMonthRef = useRef(null);
        const increaseMonthRef = useRef(null);
        const years = getYears();

        useEffect(() => {
            const handleWheel = (e) => {
                const calendarElement = document.querySelector(".react-datepicker__month-container");
                if (calendarElement && calendarElement.contains(e.target)) {
                    e.preventDefault();
                    if (e.deltaY > 0 && increaseMonthRef.current) {
                        increaseMonthRef.current();
                    } else if (e.deltaY < 0 && decreaseMonthRef.current) {
                        decreaseMonthRef.current();
                    }
                }
            };

            document.addEventListener("wheel", handleWheel, { passive: false });
            return () => {
                document.removeEventListener("wheel", handleWheel);
            };
        }, []);

        let status = "normal";

        if (disabled) {
            status = "disabled";
        } else if (props.error) {
            status = "error";
        } else if (isFocused) {
            status = "focus";
        } else if (value) {
            status = "filled";
        }

        const handleIconClick = () => {
            if (pickerRef.current) {
                pickerRef.current.setOpen(true);
            }
        };

        useEffect(() => {
            const handleClickOutside = (event) => {
                if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                    setIsFocused(false);
                }
            };

            document.addEventListener("click", handleClickOutside);
            return () => {
                document.removeEventListener("click", handleClickOutside);
            };
        }, []);

        useEffect(() => {
            if (value && date === null) {
                setDate(new Date(value));
            }
        }, [value]);

        const highlightedDateObjects =
            highlightedDates
                ?.map((date) => new Date(date))
                .filter((date) => {
                    if (!value) return true;
                    const selectedDate = new Date(value);
                    return !(
                        date.getDate() === selectedDate.getDate() &&
                        date.getMonth() === selectedDate.getMonth() &&
                        date.getFullYear() === selectedDate.getFullYear()
                    );
                }) || [];

        const handleDateChange = (date) => {
            if (date) {
                // UTC timezone'a dönüştür
                const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
                onChange(utcDate.getTime());
                setDate(date);
            } else {
                onChange(null);
                setDate(null);
            }
        };

        const handlePrevDay = () => {
            const base = date ? new Date(date) : new Date();
            const d = new Date(base);
            d.setDate(d.getDate() - 1);
            handleDateChange(d);
        };

        const handleNextDay = () => {
            const base = date ? new Date(date) : new Date();
            const d = new Date(base);
            d.setDate(d.getDate() + 1);
            handleDateChange(d);
        };

        const DateTextOnlyInput = React.forwardRef(({ date: navDate, formatDate, onClick, ...rest }, inputRef) => (
            <DayNavDateText ref={inputRef} onClick={onClick} $clickable {...rest}>
                {navDate ? formatDate(navDate) : ""}
            </DayNavDateText>
        ));

        if (variant === "move-dropdown") {
            return (
                <MoveDropdownDatePickerWrapper componentVariant={componentVariant}>
                    <DatePicker
                        selected={date}
                        onChange={handleDateChange}
                        inline
                        useWeekdaysShort={true}
                        renderCustomHeader={({ monthDate, decreaseMonth, increaseMonth, changeYear, changeMonth }) => {
                            decreaseMonthRef.current = decreaseMonth;
                            increaseMonthRef.current = increaseMonth;
                            return (
                                <StyledDatePickerHeader>
                                    <StyledDatePickerHeaderButton onClick={decreaseMonth}>
                                        <FeatherIcon aria-label="x" icon="chevron-left" size={20} />
                                    </StyledDatePickerHeaderButton>
                                    {showMonthYearDropdown ? (
                                        <StyledHeaderDropdownContainer>
                                            <StyledHeaderSelect
                                                value={monthDate.getMonth()}
                                                onChange={({ target: { value } }) => changeMonth(value)}
                                            >
                                                {months.map((month, index) => (
                                                    <option key={month} value={index}>
                                                        {month}
                                                    </option>
                                                ))}
                                            </StyledHeaderSelect>
                                            <StyledHeaderSelect
                                                value={monthDate.getFullYear()}
                                                onChange={({ target: { value } }) => changeYear(value)}
                                            >
                                                {years.map((year) => (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                ))}
                                            </StyledHeaderSelect>
                                        </StyledHeaderDropdownContainer>
                                    ) : (
                                        <StyledMonthName>
                                            {monthDate.toLocaleString("en-US", { month: "long" })}{" "}
                                            {monthDate.getFullYear()}
                                        </StyledMonthName>
                                    )}
                                    <StyledDatePickerHeaderButton onClick={increaseMonth}>
                                        <FeatherIcon aria-label="x" icon="chevron-right" size={20} />
                                    </StyledDatePickerHeaderButton>
                                </StyledDatePickerHeader>
                            );
                        }}
                        {...props}
                    />
                </MoveDropdownDatePickerWrapper>
            );
        }

        return variant !== "unstyled" ? (
            <DatePickerWrapper
                ref={datePickerRef}
                variant={status}
                disabled={disabled}
                componentVariant={"l3rnTech"}
                {...props}
            >
                <>
                    <IconContainer
                        filled={status === "filled"}
                        focus={isFocused}
                        aria-label="search-click"
                        data-testid="left-icon"
                        className="inputLeftIcon"
                        onClick={handleIconClick}
                        tabIndex="0"
                        componentVariant={componentVariant}
                    >
                        <SVG title="Emoticon" src={toAbsoluteUrl("/media/svg/popleads/calendar(1).svg")} />
                    </IconContainer>
                    <Seperator />
                </>
                <StyledDatePicker
                    ref={pickerRef}
                    selected={date}
                    showTimeSelect={noTimeSelection ? false : timeStamp}
                    timeFormat="HH:mm"
                    locale={enUS}
                    dateFormat={noTimeSelection ? "dd/MM/yyyy" : dateFormat || "dd/MM/yyyy HH:mm"}
                    timeIntervals={30}
                    onChange={(date) => handleDateChange(date, timeStamp)}
                    placeholderText={props.placeholder}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onClickOutside={() => setIsFocused(false)}
                    useWeekdaysShort={true}
                    highlightDates={highlightedDateObjects}
                    renderCustomHeader={({ monthDate, decreaseMonth, increaseMonth, changeYear, changeMonth }) => {
                        decreaseMonthRef.current = decreaseMonth;
                        increaseMonthRef.current = increaseMonth;
                        return (
                            <StyledDatePickerHeader>
                                <StyledDatePickerHeaderButton onClick={decreaseMonth}>
                                    <FeatherIcon aria-label="x" icon="chevron-left" size={20} />
                                </StyledDatePickerHeaderButton>
                                {showMonthYearDropdown ? (
                                    <StyledHeaderDropdownContainer>
                                        <StyledHeaderSelect
                                            value={monthDate.getMonth()}
                                            onChange={({ target: { value } }) => changeMonth(value)}
                                        >
                                            {months.map((month, index) => (
                                                <option key={month} value={index}>
                                                    {month}
                                                </option>
                                            ))}
                                        </StyledHeaderSelect>
                                        <StyledHeaderSelect
                                            value={monthDate.getFullYear()}
                                            onChange={({ target: { value } }) => changeYear(value)}
                                        >
                                            {years.map((year) => (
                                                <option key={year} value={year}>
                                                    {year}
                                                </option>
                                            ))}
                                        </StyledHeaderSelect>
                                    </StyledHeaderDropdownContainer>
                                ) : (
                                    <StyledMonthName>
                                        {monthDate.toLocaleString("en-US", { month: "long" })} {monthDate.getFullYear()}
                                    </StyledMonthName>
                                )}
                                <StyledDatePickerHeaderButton onClick={increaseMonth}>
                                    <FeatherIcon aria-label="x" icon="chevron-right" size={20} />
                                </StyledDatePickerHeaderButton>
                            </StyledDatePickerHeader>
                        );
                    }}
                />
                <IconContainer
                    aria-label="x-click"
                    data-testid="right-icon"
                    className="inputRightIcon"
                    onClick={props.rightIconClick}
                    componentVariant={componentVariant}
                >
                    {rightIcon && <FeatherIcon aria-label="x" icon={rightIcon} size={20} />}
                </IconContainer>
            </DatePickerWrapper>
        ) : (
            <UnStyledDatePickerWrapper ref={datePickerRef} {...props} componentVariant={componentVariant}>
                <DayNavBar>
                    <DayNavArrowButton type="button" onClick={handlePrevDay} aria-label="Previous day">
                        <FeatherIcon icon="chevron-left" size={20} />
                    </DayNavArrowButton>
                    <UnStyledDatePicker
                        ref={ref}
                        selected={date}
                        showTimeSelect={noTimeSelection ? false : timeStamp}
                        dateFormat={noTimeSelection ? "MMM d, yyyy - EEEE" : "MMM d, yyyy - EEEE"}
                        timeIntervals={30}
                        onChange={handleDateChange}
                        placeholderText={props.placeholder}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        onClickOutside={() => setIsFocused(false)}
                        useWeekdaysShort={true}
                        highlightDates={highlightedDateObjects}
                        customInput={<DateTextOnlyInput date={date} formatDate={formatDateForHeader} />}
                        renderCustomHeader={({ monthDate, decreaseMonth, increaseMonth, changeYear, changeMonth }) => {
                            decreaseMonthRef.current = decreaseMonth;
                            increaseMonthRef.current = increaseMonth;
                            return (
                                <StyledDatePickerHeader>
                                    <StyledDatePickerHeaderButton onClick={decreaseMonth}>
                                        <FeatherIcon aria-label="x" icon="chevron-left" size={20} />
                                    </StyledDatePickerHeaderButton>
                                    {showMonthYearDropdown ? (
                                        <StyledHeaderDropdownContainer>
                                            <StyledHeaderSelect
                                                value={monthDate.getMonth()}
                                                onChange={({ target: { value } }) => changeMonth(value)}
                                            >
                                                {months.map((month, index) => (
                                                    <option key={month} value={index}>
                                                        {month}
                                                    </option>
                                                ))}
                                            </StyledHeaderSelect>
                                            <StyledHeaderSelect
                                                value={monthDate.getFullYear()}
                                                onChange={({ target: { value } }) => changeYear(value)}
                                            >
                                                {years.map((year) => (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                ))}
                                            </StyledHeaderSelect>
                                        </StyledHeaderDropdownContainer>
                                    ) : (
                                        <StyledMonthName>
                                            {monthDate.toLocaleString("en-US", { month: "long" })}{" "}
                                            {monthDate.getFullYear()}
                                        </StyledMonthName>
                                    )}
                                    <StyledDatePickerHeaderButton onClick={increaseMonth}>
                                        <FeatherIcon aria-label="x" icon="chevron-right" size={20} />
                                    </StyledDatePickerHeaderButton>
                                </StyledDatePickerHeader>
                            );
                        }}
                    />
                    <DayNavArrowButton type="button" onClick={handleNextDay} aria-label="Next day">
                        <FeatherIcon icon="chevron-right" size={20} />
                    </DayNavArrowButton>
                </DayNavBar>
            </UnStyledDatePickerWrapper>
        );
    }
);

export default CustomDatePicker;
